import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Collapse,
  Drawer,
  IconButton,
  styled,
  Typography,
  useTheme,
  darken
} from "@mui/material"

import {
  CloseRounded,
  MenuRounded,
  ExpandMore,
  ExpandLess
} from "@mui/icons-material"

import HKVLink from "../HKVLink"

import { useMatch, useLocation } from "react-router-dom"

const StyledMobileMenu = styled("div", {
})(({ theme }) => {
  return {
    display: "none",
    [theme.breakpoints.down(1000)]: {
      display: "block"
    }
  }
})

const StyledMenuItem = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active"
})(({ active, theme, color }) => {
  return {
    fontWeight: 500,
    cursor: "pointer",
    padding: theme.spacing(0, 1),
    color: active ? (color ? darken(color, 0.1) : theme.palette.primary.main) : (color ? color : "#212121"),
    "&:hover": {
      color: active ? (color ? darken(color, 0.2) : theme.palette.primary.main) : (color ? color : theme.palette.primary.main)
    }
  }
})

const StyledSubMenuItem = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active"
})(({ active, theme }) => {
  return {
    fontWeight: 500,
    fontSize: "1rem",
    cursor: "pointer",
    padding: theme.spacing(0, 1),
    color: active ? theme.palette.primary.main : "#212121",
    "&:hover": {
      color: theme.palette.primary.main
    }
  }
})

const ExpandableMenuItem = ({ menuItem, t, currentPath, onNavigate }) => {
  const [expanded, setExpanded] = useState(() =>
    menuItem.menuItems?.some((sub) => sub.path && currentPath === `/${t(sub.path)}`)
  )

  const isActive = menuItem.menuItems?.some(
    (sub) => sub.path && currentPath === `/${t(sub.path)}`
  )

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          cursor: "pointer",
          width: "fit-content"
        }}
      >
        <StyledMenuItem
          className="transition"
          active={!!isActive}
          variant="h6"
        >
          {t(menuItem.label)}
        </StyledMenuItem>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </div>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            paddingLeft: 16,
            paddingTop: 12
          }}
        >
          {menuItem.menuItems.map((sub, ix) => {
            const subActive = sub.path && currentPath === `/${t(sub.path)}`
            return (
              <HKVLink
                key={ix}
                to={`/${t(sub.path)}`}
                title={t(sub.label)}
                onClick={onNavigate}
              >
                <StyledSubMenuItem
                  className="transition"
                  active={!!subActive}
                  sx={{ width: "fit-content" }}
                >
                  {t(sub.label)}
                </StyledSubMenuItem>
              </HKVLink>
            )
          })}
        </div>
      </Collapse>
    </div>
  )
}

const LeafMenuItem = ({ menuItem, t, onNavigate }) => {
  const match = useMatch(`/${menuItem.path}`)

  return (
    <HKVLink
      to={`/${menuItem.path}`}
      title={t(menuItem.label)}
      onClick={onNavigate}
    >
      <StyledMenuItem
        className="transition"
        active={!!match}
        variant="h6"
        sx={{ width: "fit-content" }}
      >
        {t(menuItem.label)}
      </StyledMenuItem>
    </HKVLink>
  )
}

const MobileMenu = (props) => {
  const { menuItems } = props

  const theme = useTheme()
  const { t } = useTranslation()
  const location = useLocation()

  const [drawerOpen, setDrawerOpen] = useState(false)

  const closeDrawer = () => setDrawerOpen(false)

  return (
    <StyledMobileMenu>
      <IconButton
        title={t("Open_menu")}
        color="primary"
        onClick={() => setDrawerOpen(!drawerOpen)}
      >
        <MenuRounded />
      </IconButton>
      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
      >
        <div
          className="menu-items"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(3),
            padding: theme.spacing(8),
            color: theme.palette.text.primary
          }}
        >
          <IconButton
            title={t("Close_menu")}
            color="inherit"
            onClick={closeDrawer}
            sx={{ position: "absolute", top: theme.spacing(1), right: theme.spacing(1) }}
          >
            <CloseRounded />
          </IconButton>

          <LeafMenuItem
            menuItem={{ path: "/", label: "Home" }}
            t={t}
            onNavigate={closeDrawer}
          />

          {menuItems.map((menuItem, ix) => {
            if (!menuItem.path && Array.isArray(menuItem.menuItems) && menuItem.menuItems.length > 0) {
              return (
                <ExpandableMenuItem
                  key={ix}
                  menuItem={menuItem}
                  t={t}
                  currentPath={location.pathname}
                  onNavigate={closeDrawer}
                />
              )
            }

            if (!menuItem.path) return null

            return (
              <LeafMenuItem
                key={ix}
                menuItem={menuItem}
                t={t}
                onNavigate={closeDrawer}
              />
            )
          })}
        </div>
      </Drawer>
    </StyledMobileMenu>
  )
}

export default React.memo(MobileMenu)